import { useEffect, useState } from 'react';
import type { PostPlatform, PostStatus } from '../../types/publishing';
import { MultiSelectFilter } from '../../components/ui/MultiSelectFilter';
import { Input } from '../../components/ui/input';

// Publishing — left filter rail (2026-07-19), matching the Community Inbox's
// FilterRail shape. Same semantics as the old inline filter bar (account /
// platform / status / search) — only the placement + presentation changed, so
// nothing about how the calendar filters posts is new here.

const PLATFORM_OPTIONS = [
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'googlebusiness', label: 'Google Business' },
];

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'publishing', label: 'Publishing' },
  { value: 'published', label: 'Published' },
  { value: 'partial', label: 'Partial' },
  { value: 'failed', label: 'Failed' },
];

export interface PublishingFilters {
  platforms: PostPlatform[];
  statuses: PostStatus[];
  search: string;
  accountSearch: string;
}

export function PublishingFilterRail({
  filters,
  onChange,
}: {
  filters: PublishingFilters;
  onChange: (next: PublishingFilters) => void;
}) {
  const [searchDraft, setSearchDraft] = useState(filters.search);

  // 300ms debounce on free-text caption search — the inbox rail's behaviour.
  useEffect(() => {
    const handle = setTimeout(() => {
      const trimmed = searchDraft.trim();
      if (filters.search !== trimmed) onChange({ ...filters, search: trimmed });
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDraft]);

  return (
    // shrink-0: this sits inside the rail's `overflow-y-auto` column, where flex
    // children shrink by DEFAULT — without it the controls squash instead of the
    // rail scrolling (the documented community-rail gotcha).
    <div className="flex w-full shrink-0 flex-col gap-3">
      <Input
        value={searchDraft}
        onChange={(e) => setSearchDraft(e.target.value)}
        placeholder="Search captions…"
        aria-label="Search post captions"
      />
      <MultiSelectFilter
        label="Platform"
        options={PLATFORM_OPTIONS}
        selected={filters.platforms}
        onChange={(next) => onChange({ ...filters, platforms: next as PostPlatform[] })}
      />
      <MultiSelectFilter
        label="Status"
        options={STATUS_OPTIONS}
        selected={filters.statuses}
        onChange={(next) => onChange({ ...filters, statuses: next as PostStatus[] })}
      />
    </div>
  );
}
